// src/context/LocationContext.js

import { createContext, useContext, useState, useEffect } from "react";
import { getUserAddressByUserId } from "../services/Navbar/LocationServices";
import { UserContext } from "./UserContext";

const LocationContext = createContext();

export function LocationProvider({ children }) {
  const { user } = useContext(UserContext);

  const getInitialSelectedCity = () => {
    try {
      const saved = localStorage.getItem("selectedCity");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };

  const [liveCoords, setLiveCoords] = useState({ lat: null, lng: null });
  const [selectedCity, setSelectedCity] = useState(null);
  const [coords, setCoords] = useState({
    lat: null,
    lng: null,
  });
  const [address, setAddress] = useState("Detecting location...");
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const GOOGLE_API_KEY = "AIzaSyAlqS1FMKTkCJoaOHzghrXA_6PA6hflHCo";

  // ✅ DEFAULT FALLBACK LOCATION (Noida / Lucknow UP)
  const DEFAULT_LOCATION = {
    lat: 28.5355,
    lng: 77.3910,
    address: "Noida, Uttar Pradesh"
  };

  // ================= FAST IP GEOLOCATION (Instant Automatic Detection) =================
  const getIpLocation = async () => {
    try {
      const res = await fetch("https://ipapi.co/json/", { signal: AbortSignal.timeout(3500) });
      const data = await res.json();
      if (data && data.latitude && data.longitude) {
        return {
          lat: parseFloat(data.latitude),
          lng: parseFloat(data.longitude),
          address: [data.city, data.region].filter(Boolean).join(", ") || data.city || "Current Location",
          cityName: data.city || "Current Location"
        };
      }
    } catch (e) {
      console.warn("ipapi fallback:", e);
    }

    try {
      const res2 = await fetch("https://ipwho.is/", { signal: AbortSignal.timeout(3500) });
      const data2 = await res2.json();
      if (data2 && data2.success !== false && data2.latitude && data2.longitude) {
        return {
          lat: parseFloat(data2.latitude),
          lng: parseFloat(data2.longitude),
          address: [data2.city, data2.region].filter(Boolean).join(", ") || data2.city || "Current Location",
          cityName: data2.city || "Current Location"
        };
      }
    } catch (e) {
      console.warn("ipwho fallback:", e);
    }

    return null;
  };

  // ================= LIVE GPS LOCATION =================
  const getLiveCoords = () => {
    return new Promise((resolve, reject) => {
      console.log("🌍 Getting LIVE location from browser GPS...");

      if (!navigator.geolocation) {
        console.log("❌ Geolocation not supported");
        reject(new Error("Geolocation not supported"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const data = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          };
          console.log("🌍 LIVE GPS COORDS ACQUIRED:", data);
          resolve(data);
        },
        (err) => {
          console.log("❌ GPS ERROR:", err.message);
          reject(err);
        },
        { 
          enableHighAccuracy: true, 
          timeout: 6000,
          maximumAge: 60000 
        }
      );
    });
  };

  // ================= ADDRESS REVERSE GEOCODING =================
  const getAddressFromCoords = async (lat, lng) => {
    // 1. Try Google Maps Geocoding
    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_API_KEY}`
      );
      const data = await res.json();
      if (data?.results?.[0]?.formatted_address) {
        return data.results[0].formatted_address;
      }
    } catch (err) {
      console.warn("Google geocoding error, falling back to OSM Nominatim:", err);
    }

    // 2. Free accurate fallback: OpenStreetMap Nominatim
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
        { headers: { "Accept-Language": "en" } }
      );
      const data = await res.json();
      if (data?.display_name) {
        const addr = data.address;
        const cityOrArea = addr?.city || addr?.town || addr?.suburb || addr?.neighbourhood || addr?.state_district || "Noida";
        const state = addr?.state || "";
        return `${cityOrArea}${state ? `, ${state}` : ""}`;
      }
    } catch (e) {
      console.error("OSM geocoding failed:", e);
    }

    return "Current Location";
  };

  // ================= FETCH LOCATION =================
  const fetchLocation = async (forceGps = false) => {
    setLoading(true);
    setError("");

    try {
      // 1. If forceGps is false and user in current session explicitly selected a city, respect it
      if (!forceGps && selectedCity && selectedCity.latitude && selectedCity.longitude) {
        const lat = parseFloat(selectedCity.latitude);
        const lng = parseFloat(selectedCity.longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          setCoords({ lat, lng });
          setAddress(selectedCity.name || selectedCity.cityName || "Selected City");
          setLoading(false);
          return;
        }
      }

      // 2. Always auto-fetch Live Location (Instant IP first, then GPS refinement)
      let live = null;
      
      // Step A: Fast IP detect for instant UI display
      getIpLocation().then((ipLoc) => {
        if (ipLoc && !live && !selectedCity) {
          setCoords({ lat: ipLoc.lat, lng: ipLoc.lng });
          setAddress(ipLoc.address);
          console.log("⚡ Fast IP Live Location set:", ipLoc);
        }
      }).catch(() => {});

      // Step B: High accuracy browser GPS
      try {
        live = await getLiveCoords();
        setLiveCoords(live);

        const liveAddress = await getAddressFromCoords(live.lat, live.lng);
        setSelectedCity(null);
        localStorage.removeItem("selectedCity");
        setAddress(liveAddress);
        setCoords(live);
        console.log("✅ Live GPS Location Active:", live, liveAddress);
      } catch (err) {
        console.log("⚠️ Live browser GPS denied/failed, using IP location or user saved address");
        if (!selectedCity) {
          const ipLoc = await getIpLocation();
          if (ipLoc) {
            setCoords({ lat: ipLoc.lat, lng: ipLoc.lng });
            setAddress(ipLoc.address);
            setLoading(false);
            return;
          }

          if (user?.id || user?.userId) {
            try {
              const uid = user?.id || user?.userId;
              const data = await getUserAddressByUserId(uid);
              const list = Array.isArray(data) ? data : data?.data || [];
              if (list.length && list[0]?.latitude && list[0]?.longitude) {
                const first = list[0];
                const dbCoords = { lat: parseFloat(first.latitude), lng: parseFloat(first.longitude) };
                setCoords(dbCoords);
                setAddress([first.addressTitle, first.locality, first.street, first.pincode].filter(Boolean).join(", "));
                setLoading(false);
                return;
              }
            } catch (e) {}
          }

          // Fallback default
          setCoords({
            lat: DEFAULT_LOCATION.lat,
            lng: DEFAULT_LOCATION.lng,
          });
          setAddress(DEFAULT_LOCATION.address);
        }
      }

    } catch (err) {
      console.log("❌ Location fetch failed:", err);
      if (!selectedCity) {
        setCoords({
          lat: DEFAULT_LOCATION.lat,
          lng: DEFAULT_LOCATION.lng,
        });
        setAddress(DEFAULT_LOCATION.address);
      }
    } finally {
      setLoading(false);
    }
  };

  // ================= FETCH SAVED ADDRESSES =================
  const loadSavedAddresses = async () => {
    const userId = user?.id || user?.userId;
    if (!userId) return [];
    try {
      const data = await getUserAddressByUserId(userId);
      const list = Array.isArray(data) ? data : data?.data || [];
      setSavedAddresses(list);
      return list;
    } catch (e) {
      console.error("Failed to load saved addresses:", e);
      return [];
    }
  };

  // ================= SELECT SPECIFIC USER SAVED ADDRESS =================
  const setAddressManually = (savedAddr) => {
    if (!savedAddr) return;
    const lat = parseFloat(savedAddr.latitude);
    const lng = parseFloat(savedAddr.longitude);
    const fullText = [
      savedAddr.addressTitle,
      savedAddr.faltHousNumber,
      savedAddr.street,
      savedAddr.locality,
      savedAddr.pincode,
    ]
      .filter(Boolean)
      .join(", ") || savedAddr.address || "Selected Address";

    const addrData = {
      name: fullText,
      cityName: savedAddr.locality || savedAddr.addressTitle || "Selected Address",
      latitude: !isNaN(lat) ? lat : DEFAULT_LOCATION.lat,
      longitude: !isNaN(lng) ? lng : DEFAULT_LOCATION.lng,
      isUserAddress: true,
      addressId: savedAddr.id,
    };

    setSelectedCity(addrData);
    setCoords({ lat: addrData.latitude, lng: addrData.longitude });
    setAddress(fullText);
    localStorage.setItem("selectedCity", JSON.stringify(addrData));
  };

  const CITY_COORDS = {
    "lucknow": { lat: 26.8467, lng: 80.9462 },
    "delhi": { lat: 28.6139, lng: 77.2090 },
    "new delhi": { lat: 28.6139, lng: 77.2090 },
    "noida": { lat: 28.5355, lng: 77.3910 },
    "greater noida": { lat: 28.4744, lng: 77.5040 },
    "gurugram": { lat: 28.4595, lng: 77.0266 },
    "gurgaon": { lat: 28.4595, lng: 77.0266 },
    "ghaziabad": { lat: 28.6692, lng: 77.4538 },
    "kanpur": { lat: 26.4499, lng: 80.3319 },
    "varanasi": { lat: 25.3176, lng: 82.9739 },
    "banaras": { lat: 25.3176, lng: 82.9739 },
    "prayagraj": { lat: 25.4358, lng: 81.8463 },
    "allahabad": { lat: 25.4358, lng: 81.8463 },
    "gorakhpur": { lat: 26.7606, lng: 83.3732 },
    "agra": { lat: 27.1767, lng: 78.0081 },
    "bareilly": { lat: 28.3670, lng: 79.4304 },
    "aligarh": { lat: 27.8974, lng: 78.0880 },
    "moradabad": { lat: 28.8386, lng: 78.7733 },
    "meerut": { lat: 28.9845, lng: 77.7064 },
    "faizabad": { lat: 26.7731, lng: 82.1460 },
    "ayodhya": { lat: 26.7922, lng: 82.1998 },
    "patna": { lat: 25.5941, lng: 85.1376 },
    "jaipur": { lat: 26.9124, lng: 75.7873 },
    "mumbai": { lat: 19.0760, lng: 72.8777 },
    "bengaluru": { lat: 12.9716, lng: 77.5946 },
    "bangalore": { lat: 12.9716, lng: 77.5946 },
    "kolkata": { lat: 22.5726, lng: 88.3639 },
    "hyderabad": { lat: 17.3850, lng: 78.4867 },
    "chennai": { lat: 13.0827, lng: 80.2707 },
    "pune": { lat: 18.5204, lng: 73.8567 },
    "ahmedabad": { lat: 23.0225, lng: 72.5714 },
    "indore": { lat: 22.7196, lng: 75.8577 },
    "bhopal": { lat: 23.2599, lng: 77.4126 },
    "chandigarh": { lat: 30.7333, lng: 76.7794 },
    "dehradun": { lat: 30.3165, lng: 78.0322 },
  };

  // ================= CITY SELECT =================
  const setCityManually = async (city) => {
    const cityName = city?.name || city?.cityName || city?.city || (typeof city === 'string' ? city : "Selected City");
    const cleanKey = cityName.toLowerCase().trim();

    let lat = parseFloat(city?.latitude || city?.lat || city?.Latitude || city?.Lat);
    let lng = parseFloat(city?.longitude || city?.lng || city?.longnitude || city?.Longnitude || city?.Longitude || city?.Lng);

    if (isNaN(lat) || isNaN(lng) || !lat || !lng) {
      if (CITY_COORDS[cleanKey]) {
        lat = CITY_COORDS[cleanKey].lat;
        lng = CITY_COORDS[cleanKey].lng;
      } else {
        const match = Object.keys(CITY_COORDS).find(k => cleanKey.includes(k) || k.includes(cleanKey));
        if (match) {
          lat = CITY_COORDS[match].lat;
          lng = CITY_COORDS[match].lng;
        } else {
          lat = DEFAULT_LOCATION.lat;
          lng = DEFAULT_LOCATION.lng;
        }
      }
    }

    const cityData = {
      name: cityName,
      cityName: cityName,
      latitude: lat,
      longitude: lng,
    };

    console.log("🏙️ CITY SELECT ACTIVATED 👉", cityData);

    setSelectedCity(cityData);
    setCoords({ lat, lng });
    setAddress(cityName);

    localStorage.setItem("selectedCity", JSON.stringify(cityData));
  };

  // ================= CLEAR CITY (SWITCH TO LIVE GPS) =================
  const clearManualCity = () => {
    console.log("📡 SWITCHING BACK TO LIVE LOCATION");
    setSelectedCity(null);
    localStorage.removeItem("selectedCity");
    fetchLocation(true);
  };

  // ================= UPDATE LOCATION =================
  const updateLocation = (newAddress) => {
    setAddress(newAddress);
  };

  // ================= INITIAL LOAD =================
  useEffect(() => {
    fetchLocation();
    loadSavedAddresses();
  }, [user?.id, user?.userId]);

  // ================= FINAL COORDS =================
  useEffect(() => {
    if (selectedCity?.latitude && selectedCity?.longitude) {
      setCoords({
        lat: parseFloat(selectedCity.latitude),
        lng: parseFloat(selectedCity.longitude),
      });
      console.log("✅ Using SELECTED CITY coords:", selectedCity.name);
    } else if (liveCoords.lat && liveCoords.lng) {
      setCoords(liveCoords);
      console.log("✅ Using LIVE coords");
    } else {
      setCoords({
        lat: null,
        lng: null
      });
    }
  }, [selectedCity, liveCoords]);

  // ================= DEBUG =================
  useEffect(() => {
    console.log("📍 FINAL COORDS 👉", coords);
    console.log("🏙️ ADDRESS 👉", address);
  }, [coords, address]);

  return (
    <LocationContext.Provider
      value={{
        coords,
        address,
        loading,
        error,
        fetchLocation,
        setCityManually,
        setAddressManually,
        loadSavedAddresses,
        savedAddresses,
        clearManualCity,
        selectedCity,
        updateLocation,
        setAddress,
        liveCoords, // ✅ Export for debugging
      }}
    >
      {children}
    </LocationContext.Provider>
  );
}

// ✅ FIXED HOOK
export function useLocationContext() {
  return useContext(LocationContext);
}